import { useState } from 'react';

import { api } from '../api/client';
import type {
  ComplianceTrendRow,
  FailureTrendRow,
  RegionCompliance,
  SiteCompliance,
  UserCompliance,
} from '../api/types';
import { Button, Card, Field, Loading, Notice, Percentage, Table } from '../components';
import { ComplianceTrendChart, RankedBars, VerdictMixChart } from '../components/Charts';
import { useResource } from '../hooks';
import { humanise } from '../format';
import { useSession } from '../auth/session';

/**
 * Reporting.
 *
 * Every figure here comes from a SQL query over typed columns. That is the whole reason
 * the answer model is relational: SMA held answers as a JSON blob and parsed them in
 * application memory, which produced an 8,713-line report controller, an 8GB heap and
 * requests that timed out after hours.
 *
 * Export is CSV generated in the browser from data already fetched. A server-side export
 * endpoint would be a second implementation of the same query, and the two would drift.
 */
export function Reports(): JSX.Element {
  const { config } = useSession();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // A half-specified range is not sent at all. Defaulting the missing end to "today"
  // quietly changes what a saved view means as time passes.
  const range = from !== '' && to !== '' ? `?from=${from}&to=${to}` : '';

  const trendOverTime = useResource(() => api.get<ComplianceTrendRow[]>(`/reports/compliance-trend${range}`), [range]);
  const regions = useResource(() => api.get<RegionCompliance[]>(`/reports/compliance-by-region${range}`), [range]);
  const sites = useResource(() => api.get<SiteCompliance[]>(`/reports/compliance-by-site${range}`), [range]);
  const users = useResource(() => api.get<UserCompliance[]>(`/reports/compliance-by-user${range}`), [range]);
  const trend = useResource(() => api.get<FailureTrendRow[]>(`/reports/failure-trend${range}`), [range]);

  return (
    <>
      <Card title="Filters">
        <div className="toolbar">
          <Field label="From" value={from} onChange={setFrom} type="date" />
          <Field label="To" value={to} onChange={setTo} type="date" />
          <Button
            variant="secondary"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Clear
          </Button>
        </div>
        {(from === '') !== (to === '') && (
          <Notice kind="info">Set both dates to apply a range. A half-range is ignored.</Notice>
        )}
      </Card>

      {/*
        Charts lead, because shape is what a table is worst at. These two are one reading
        rather than two: the line says whether things improved, the stack says whether the
        month it improved in had enough submissions for that to mean anything — a question
        that rarely gets asked when the two are separated. The tables below hold the numbers
        actually read off, which is also why no figure here depends on telling two colours
        apart.
      */}
      <div className="grid-2-charts">
        <Card title="Compliance over time" actions={<span className="card-note">By month</span>}>
          {trendOverTime.loading ? (
            <Loading />
          ) : trendOverTime.error !== null ? (
            <Notice kind="error">{trendOverTime.error}</Notice>
          ) : (
            <ComplianceTrendChart rows={trendOverTime.data ?? []} />
          )}
        </Card>

        <Card
          title="How submissions were decided"
          actions={
            <Button variant="secondary" onClick={() => downloadCsv('compliance-trend', trendOverTime.data ?? [])}>
              Export CSV
            </Button>
          }
        >
          {trendOverTime.loading ? (
            <Loading />
          ) : trendOverTime.error !== null ? (
            <Notice kind="error">{trendOverTime.error}</Notice>
          ) : (
            <VerdictMixChart rows={trendOverTime.data ?? []} />
          )}
        </Card>
      </div>

      {/*
        The two rankings, side by side and both bars: "which place is worst" and "which unit
        is worst" are the same question at two levels, and reading them as two tables meant
        comparing columns of percentages by eye. The tables further down still hold the
        counts behind each bar.
      */}
      <div className="grid-2 grid-2-even">
        <Card title={`Weakest ${config.siteLabel.toLowerCase()}s`}>
          {sites.loading ? (
            <Loading />
          ) : sites.error !== null ? (
            <Notice kind="error">{sites.error}</Notice>
          ) : (
            <RankedBars
              countLabel="submissions"
              emptyText="No submissions in this period."
              items={(sites.data ?? []).slice(0, 10).map((row) => ({
                key: row.siteId,
                label: row.siteName,
                sublabel: row.siteCode,
                value: row.compliancePercent,
                count: row.totalSubmissions,
              }))}
            />
          )}
        </Card>

        <Card title="Weakest units">
          {regions.loading ? (
            <Loading />
          ) : regions.error !== null ? (
            <Notice kind="error">{regions.error}</Notice>
          ) : (
            <RankedBars
              countLabel="submissions"
              emptyText="No submissions in this period."
              items={(regions.data ?? []).slice(0, 10).map((row) => ({
                key: row.orgUnitId,
                label: row.orgUnitName,
                sublabel: `${row.siteCount} ${config.siteLabel.toLowerCase()}${row.siteCount === 1 ? '' : 's'}`,
                value: row.compliancePercent,
                count: row.totalSubmissions,
              }))}
            />
          )}
        </Card>
      </div>

      <Card
        title="Compliance by unit"
        actions={
          <Button variant="secondary" onClick={() => downloadCsv('compliance-by-region', regions.data ?? [])}>
            Export CSV
          </Button>
        }
      >
        {regions.loading ? (
          <Loading />
        ) : regions.error !== null ? (
          <Notice kind="error">{regions.error}</Notice>
        ) : (
          <Table
            rows={regions.data ?? []}
            keyOf={(row) => row.orgUnitId}
            empty="No submissions in this period."
            columns={[
              { header: 'Unit', render: (row) => row.orgUnitName },
              // Without this the percentage is unreadable: one place visited forty times
              // and forty places visited once produce the same figure.
              { header: config.siteLabel + 's', numeric: true, render: (row) => row.siteCount },
              { header: 'Total', numeric: true, render: (row) => row.totalSubmissions },
              { header: 'Accepted', numeric: true, render: (row) => row.acceptedCount },
              { header: 'Flagged', numeric: true, render: (row) => row.flaggedCount },
              { header: 'Blocked', numeric: true, render: (row) => row.blockedCount },
              {
                header: 'Compliance',
                numeric: true,
                render: (row) => <Percentage value={row.compliancePercent} />,
              },
              { header: 'Last visited', render: (row) => row.lastVisitedOn ?? '—' },
            ]}
          />
        )}
      </Card>

      <Card
        title={`Compliance by ${config.siteLabel.toLowerCase()}`}
        actions={
          <Button variant="secondary" onClick={() => downloadCsv('compliance-by-site', sites.data ?? [])}>
            Export CSV
          </Button>
        }
      >
        {sites.loading ? (
          <Loading />
        ) : sites.error !== null ? (
          <Notice kind="error">{sites.error}</Notice>
        ) : (
          <Table
            rows={sites.data ?? []}
            keyOf={(row) => row.siteId}
            empty="No submissions in this period."
            columns={[
              { header: 'Code', render: (row) => row.siteCode },
              { header: config.siteLabel, render: (row) => row.siteName },
              { header: 'Unit', render: (row) => row.orgUnitName },
              { header: 'Total', numeric: true, render: (row) => row.totalSubmissions },
              { header: 'Accepted', numeric: true, render: (row) => row.acceptedCount },
              { header: 'Flagged', numeric: true, render: (row) => row.flaggedCount },
              { header: 'Blocked', numeric: true, render: (row) => row.blockedCount },
              {
                header: 'Compliance',
                numeric: true,
                render: (row) =>
                  <Percentage value={row.compliancePercent} />,
              },
              { header: 'Last visited', render: (row) => row.lastVisitedOn ?? '—' },
            ]}
          />
        )}
      </Card>

      <Card
        title={`Compliance by ${config.fieldUserLabel.toLowerCase()}`}
        actions={
          <Button variant="secondary" onClick={() => downloadCsv('compliance-by-user', users.data ?? [])}>
            Export CSV
          </Button>
        }
      >
        {users.loading ? (
          <Loading />
        ) : users.error !== null ? (
          <Notice kind="error">{users.error}</Notice>
        ) : (
          <Table
            rows={users.data ?? []}
            keyOf={(row) => row.fieldUserId}
            empty="No submissions in this period."
            columns={[
              { header: 'Name', render: (row) => row.fieldUserName },
              // The key, not the name — `field_user` was being printed as it is stored.
              { header: 'Role', render: (row) => humanise(row.role) },
              { header: 'Total', numeric: true, render: (row) => row.totalSubmissions },
              { header: 'Accepted', numeric: true, render: (row) => row.acceptedCount },
              { header: 'Blocked', numeric: true, render: (row) => row.blockedCount },
              {
                header: 'Compliance',
                numeric: true,
                render: (row) =>
                  <Percentage value={row.compliancePercent} />,
              },
            ]}
          />
        )}
      </Card>

      <Card title="What is failing">
        {trend.loading ? (
          <Loading />
        ) : trend.error !== null ? (
          <Notice kind="error">{trend.error}</Notice>
        ) : (
          <Table
            rows={trend.data ?? []}
            keyOf={(row) => `${row.periodMonth}-${row.checkKey}`}
            empty="No failures in this period."
            columns={[
              { header: 'Month', render: (row) => row.periodMonth },
              { header: 'Check', render: (row) => <code>{row.checkKey}</code> },
              { header: 'Runs', numeric: true, render: (row) => row.totalRuns },
              { header: 'Blocked', numeric: true, render: (row) => row.blockCount },
              { header: 'Flagged', numeric: true, render: (row) => row.flagCount },
              // How widely a failure is spread separates a broken process from one bad site.
              { header: 'Sites affected', numeric: true, render: (row) => row.sitesAffected },
            ]}
          />
        )}
      </Card>

      <p className="muted">
        These figures read the same `reporting` views a business-intelligence tool would
        connect to, so a dashboard built elsewhere and this screen cannot disagree.
      </p>
    </>
  );
}

/** CSV from the rows already on screen — no second query to drift from the first. */
function downloadCsv<T extends object>(name: string, rows: readonly T[]): void {
  if (rows.length === 0) return;

  const headers = Object.keys(rows[0] as object);
  const escape = (value: unknown): string => {
    const text = value === null || value === undefined ? '' : String(value);
    // Quote when the value could otherwise break the row apart.
    return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };

  const csv = [
    headers.join(','),
    ...rows.map((row) => headers.map((header) => escape((row as Record<string, unknown>)[header])).join(',')),
  ].join('\n');

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
